import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
type Product = {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
};
type ToastData = {
  id: number;
  name: string;
  city: string;
  product: Product;
};
const ARGENTINE_NAMES = ['María José', 'Sofía', 'Camila', 'Valentina', 'Lucía', 'Martina', 'Florencia', 'Agustina', 'Carolina', 'Julieta', 'Mariana', 'Brenda', 'Daniela', 'Romina', 'Belén', 'Gabriela', 'Paula', 'Micaela', 'Cecilia', 'Verónica', 'Natalia', 'Yamila', 'Rocío', 'Antonella', 'Laura', 'Andrea', 'Silvina', 'Estefanía', 'Tamara', 'Noelia', 'Mariela', 'Vanesa', 'Patricia', 'Lorena', 'Carla', 'Soledad', 'Cintia', 'Jésica', 'Macarena', 'Aldana', 'Juan', 'Martín', 'Diego', 'Federico', 'Nicolás', 'Matías', 'Sebastián', 'Lucas', 'Tomás', 'Joaquín', 'Santiago', 'Agustín', 'Facundo', 'Ezequiel', 'Maximiliano', 'Gonzalo', 'Leandro', 'Hernán', 'Ariel', 'Cristian'];
const ARGENTINE_PROVINCES = ['Buenos Aires', 'CABA', 'Córdoba', 'Santa Fe', 'Mendoza', 'Tucumán', 'Salta', 'Entre Ríos', 'Misiones', 'Chaco', 'Corrientes', 'Santiago del Estero', 'San Juan', 'Jujuy', 'Río Negro', 'Neuquén', 'Formosa', 'Chubut', 'San Luis', 'Catamarca', 'La Rioja', 'La Pampa', 'Santa Cruz', 'Tierra del Fuego'];
const FALLBACK_PRODUCTS: Product[] = [{
  title: 'Historias infantiles para dormir en paz',
  editionLabel: 'Ebook · 33 cuentos',
  priceNumber: 12990,
  priceText: '$12.990 ARS',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/t15Zhok8bgmfUUe93G46yx/PORTADA_LIBRO_33_CUENTOS.png'
}, {
  title: 'Pack Completo',
  editionLabel: 'Ebook + 20 Oraciones',
  priceNumber: 17480,
  priceText: '$17.480 ARS',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/t15Zhok8bgmfUUe93G46yx/PORTADA_LIBRO_33_CUENTOS.png'
}];
// Parse price strings like "$12.990", "$17 USD", "27 USD", "USD 27.00"
function parsePrice(text: string): {
  number: number;
  text: string;
} | null {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  // Match formats: $12.990 / $12,990.00 / $27 / 27 USD / USD 27.00
  const match = cleaned.match(/(?:\$|USD|ARS|EUR|€)?\s*(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})?|\d+(?:[.,]\d{1,2})?)\s*(USD|ARS|EUR)?/i);
  if (!match) return null;
  const raw = match[1];
  // Detect if last separator is decimal or thousand
  let num: number;
  const lastDot = raw.lastIndexOf('.');
  const lastComma = raw.lastIndexOf(',');
  if (lastDot === -1 && lastComma === -1) {
    num = parseInt(raw, 10);
  } else {
    // Argentine format $12.990 → 12990 (no decimals or decimals after comma)
    // If last separator followed by 3 digits → thousand separator
    const lastSep = Math.max(lastDot, lastComma);
    const after = raw.length - lastSep - 1;
    if (after === 3) {
      num = parseInt(raw.replace(/[.,]/g, ''), 10);
    } else {
      // Decimal — normalize comma to dot
      num = parseFloat(raw.replace(/\.(?=\d{3}(\D|$))/g, '').replace(/,(?=\d{3}(\D|$))/g, '').replace(',', '.'));
    }
  }
  if (!isFinite(num) || num <= 0) return null;
  return {
    number: num,
    text: cleaned
  };
}
function scanProducts(): Product[] {
  if (typeof document === 'undefined') return FALLBACK_PRODUCTS;
  const products: Product[] = [];
  // 1) JSON-LD
  try {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(s => {
      try {
        const data = JSON.parse(s.textContent || '{}');
        const items = Array.isArray(data) ? data : [data];
        items.forEach((item: any) => {
          if (item && (item['@type'] === 'Product' || item.type === 'Product')) {
            const offers = item.offers ? Array.isArray(item.offers) ? item.offers : [item.offers] : [];
            offers.forEach((offer: any) => {
              const priceNum = parseFloat(offer.price);
              if (!isFinite(priceNum)) return;
              products.push({
                title: item.name || 'Producto',
                editionLabel: offer.name || item.category || 'Edición',
                priceNumber: priceNum,
                priceText: `${offer.priceCurrency || '$'} ${offer.price}`,
                thumbnailUrl: Array.isArray(item.image) ? item.image[0] : item.image || ''
              });
            });
            if (offers.length === 0 && item.image) {
              products.push({
                title: item.name || 'Producto',
                editionLabel: item.category || 'Edición',
                priceNumber: 0,
                priceText: '',
                thumbnailUrl: Array.isArray(item.image) ? item.image[0] : item.image
              });
            }
          }
        });
      } catch {}
    });
  } catch {}
  // 2) DOM scan — look for elements with price-like text near images
  if (products.length === 0) {
    const priceRegex = /(?:\$|USD|ARS|€)\s*\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})?/;
    const candidates = Array.from(document.querySelectorAll('section, article, div')).filter(el => {
      const text = (el.textContent || '').slice(0, 600);
      if (!priceRegex.test(text)) return false;
      const ctaRegex = /(comprar|añadir|agregar al carrito|checkout|quiero|acceso|llev|order|buy)/i;
      return ctaRegex.test(text);
    }).slice(0, 6);
    candidates.forEach(el => {
      const img = el.querySelector('img') as HTMLImageElement | null;
      const heading = el.querySelector('h1, h2, h3, h4') as HTMLElement | null;
      const priceEls = Array.from(el.querySelectorAll('*')).filter(e => {
        const t = e.textContent || '';
        return priceRegex.test(t) && t.length < 60 && !e.querySelector('*');
      });
      const priceEl = priceEls[0] as HTMLElement | undefined;
      if (!priceEl || !img) return;
      const parsed = parsePrice(priceEl.textContent || '');
      if (!parsed) return;
      const title = heading?.textContent?.trim() || document.title || 'Producto';
      // Try to detect edition label — sibling text
      const editionCandidates = el.querySelectorAll('span, p, h4, h5');
      let editionLabel = '';
      editionCandidates.forEach(ec => {
        const t = (ec.textContent || '').trim();
        if (!editionLabel && t.length > 3 && t.length < 60 && !priceRegex.test(t) && /(ebook|digital|física|fisico|pack|edición|edition|libro)/i.test(t)) {
          editionLabel = t;
        }
      });
      products.push({
        title: title.slice(0, 80),
        editionLabel: editionLabel || 'Edición Digital',
        priceNumber: parsed.number,
        priceText: parsed.text,
        thumbnailUrl: img.src
      });
    });
  }
  // Dedupe by priceNumber + title
  const seen = new Set<string>();
  const deduped = products.filter(p => {
    const key = `${p.title}-${p.priceNumber}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return deduped.length > 0 ? deduped : FALLBACK_PRODUCTS;
}
function pickWeightedProduct(products: Product[]): Product {
  if (products.length === 0) return FALLBACK_PRODUCTS[0];
  if (products.length === 1) return products[0];
  // 70% first product, 30% second (or distribute evenly if more)
  if (products.length === 2) {
    return Math.random() < 0.7 ? products[0] : products[1];
  }
  return products[Math.floor(Math.random() * products.length)];
}
export function RecentPurchaseToast() {
  const [products, setProducts] = useState<Product[]>([]);
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null);
  const recentNamesRef = useRef<string[]>([]);
  const queueRef = useRef<ToastData[]>([]);
  const showingRef = useRef(false);
  const idRef = useRef(0);
  const timeoutsRef = useRef<number[]>([]);
  const reducedMotion = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Wait one frame so DOM has rendered before scanning
    const scanTimer = window.setTimeout(() => {
      const scanned = scanProducts();
      setProducts(scanned);
    }, 800);
    return () => {
      window.clearTimeout(scanTimer);
      timeoutsRef.current.forEach(t => window.clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, []);
  useEffect(() => {
    if (products.length === 0) return;
    const pickName = () => {
      const recent = recentNamesRef.current;
      const available = ARGENTINE_NAMES.filter(n => !recent.includes(n));
      const pool = available.length > 0 ? available : ARGENTINE_NAMES;
      const name = pool[Math.floor(Math.random() * pool.length)];
      recent.push(name);
      if (recent.length > 10) recent.shift();
      return name;
    };
    const pickCity = () => ARGENTINE_PROVINCES[Math.floor(Math.random() * ARGENTINE_PROVINCES.length)];
    const scheduleNext = (delay: number) => {
      const t = window.setTimeout(() => {
        const toast: ToastData = {
          id: ++idRef.current,
          name: pickName(),
          city: pickCity(),
          product: pickWeightedProduct(products)
        };
        enqueue(toast);
        // Schedule next at 8–15s
        scheduleNext(8000 + Math.random() * 7000);
      }, delay);
      timeoutsRef.current.push(t);
    };
    const enqueue = (toast: ToastData) => {
      if (showingRef.current) {
        queueRef.current.push(toast);
      } else {
        showToast(toast);
      }
    };
    const showToast = (toast: ToastData) => {
      showingRef.current = true;
      setCurrentToast(toast);
      const t = window.setTimeout(() => {
        setCurrentToast(null);
        // wait for exit animation before pulling next
        const t2 = window.setTimeout(() => {
          showingRef.current = false;
          const next = queueRef.current.shift();
          if (next) showToast(next);
        }, 600);
        timeoutsRef.current.push(t2);
      }, 4000);
      timeoutsRef.current.push(t);
    };
    scheduleNext(3000);
    return () => {
      timeoutsRef.current.forEach(t => window.clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, [products]);
  const animProps = reducedMotion.current ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.2
    }
  } : {
    initial: {
      opacity: 0,
      y: 40,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 40,
      scale: 0.95
    },
    transition: {
      duration: 0.45,
      ease: [0.16, 1, 0.3, 1] as const
    }
  };
  return <div aria-live="polite" aria-atomic="true" className="pointer-events-none fixed inset-x-0 bottom-4 sm:bottom-6 z-[9999] flex justify-center px-3" data-id="element-195">
      <AnimatePresence mode="wait" data-id="element-196">
        {currentToast && <motion.div key={currentToast.id} {...animProps} className="pointer-events-auto flex items-center gap-3 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl shadow-indigo/20 border border-blush/40 px-3 py-2.5 sm:px-4 sm:py-3 max-w-sm w-full" data-id="element-197">
            <div className="relative flex-shrink-0" data-id="element-198">
              <img src={currentToast.product.thumbnailUrl} alt={currentToast.product.title} className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover" data-id="element-199" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" data-id="element-200" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-201">
              <p className="text-xs sm:text-sm text-indigo leading-snug" data-id="element-202">
                <span className="font-semibold" data-id="element-203">{currentToast.name}</span>
                <span className="text-indigo/70" data-id="element-204"> de {currentToast.city}</span>
                <span className="text-indigo/70" data-id="element-205"> acaba de comprar </span>
                <span className="font-semibold" data-id="element-206">
                  {currentToast.product.editionLabel}
                </span>
                <span className="text-indigo/70" data-id="element-207"> — </span>
                <span className="font-bold text-gold" data-id="element-208">
                  {currentToast.product.priceText}
                </span>
              </p>
              <p className="text-[10px] sm:text-xs text-indigo/50 mt-0.5" data-id="element-209">
                hace unos segundos · compra verificada
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}